import { debounce } from "lodash"
import { useEffect, useRef, useState } from "react"
import toast from "react-hot-toast"
import { TbChevronRight } from "react-icons/tb"
import { ActionButton, BaseTable, Button, Card, FooterMenu, HeaderMenu, Input, PaginationData, TableHeaderSort, TableNumber } from "components"
import { default_pagination_value, useApi, useLocation, useToggle } from "utils"
import { PaginationState, SuccessFetch } from "types"
import { DataList, ListWarehouse } from "./types"
import { CreateModal } from "./components/CreateModal"
import { UpdateModal } from "./components/UpdateModal"
import { DeleteModal } from "./components/DeleteModal"

type HeaderState = { title: string; sort: boolean; align?: "left" | "right" }

export default function Warehouse() {
  const api = useApi()
  const { data: locationOptions, isLoading: locationLoading } = useLocation()
  const [data, setData] = useState<DataList>({ items: [], pagination_data: { total: 0, page: 0, size: 0, pages: 0 } })
  const [isLoading, setLoading] = useState(true)
  const [pagination, setPagination] = useState<PaginationState>(default_pagination_value)
  const createModal = useToggle(false)
  const [editData, setEditData] = useState<ListWarehouse | null>(null)
  const [deleteData, setDeleteData] = useState<ListWarehouse | null>(null)
  const lastOkPaginationRef = useRef<PaginationState | null>(null)

  const header: HeaderState[] = [
    { title: "CODE", sort: true },
    { title: "LOCATION", sort: true },
    { title: "WAREHOUSE", sort: true },
    { title: "NOTE", sort: false },
  ]

  /** `sort` query: `warehouse_code`, `location_name`, `warehouse_name` (column:asc|desc). */
  const sortMapping: Record<string, string> = {
    CODE: "warehouse_code",
    LOCATION: "location_name",
    WAREHOUSE: "warehouse_name",
  }

  const handleSort = (item: string, sortType: string) => {
    const col = sortMapping[item]
    const sortParam = col ? `${col}:${sortType}` : ""
    setPagination(prev => ({ ...prev, sort: sortParam, page: 1 }))
  }

  const fetchData = () => {
    setLoading(true)
    const requestPagination: PaginationState = { ...pagination }
    const params: Record<string, any> = { page: requestPagination.page, limit: requestPagination.limit }
    if (requestPagination.keyword) params.keyword = requestPagination.keyword
    if (requestPagination.sort) params.sort = requestPagination.sort
    api.get("/location-warehouse/all", { params })
      .then((res: SuccessFetch<{ payload: DataList }>) => {
        setData(res.data.payload)
        lastOkPaginationRef.current = requestPagination
      })
      .catch(() => {
        if (lastOkPaginationRef.current !== null) {
          setPagination({ ...lastOkPaginationRef.current })
          toast.error("Could not load data. Showing the previous result.")
        } else {
          toast.error("Could not load data.")
        }
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => { fetchData() }, [pagination.page, pagination.limit, pagination.keyword, pagination.sort]) // eslint-disable-line

  const onDebounce = debounce((e: any) => setPagination(prev => ({ ...prev, keyword: e.target.value, page: 1 })))

  return (
    <section className="container gap-5 flex flex-col">
      <HeaderMenu title="WAREHOUSE" />

      <Card>
        <Card.Body className="flex-row justify-end items-center">
          <Button color="primary" onClick={createModal.toggle}>
            CREATE NEW <TbChevronRight />
          </Button>
        </Card.Body>

        <Card.Body>
          <div className="mb-3">
            <Input
              label="SEARCH"
              className="md:max-w-[300px] w-full"
              placeholder="Search..."
              onChange={onDebounce}
            />
          </div>

          <div className="overflow-x-auto">
            <BaseTable>
              <thead>
                <tr className="bg-black text-white text-xs">
                  <th className="text-center">NO</th>
                  <TableHeaderSort
                    title={header}
                    onSort={(item, sortType) => handleSort(item, sortType)}
                  />
                  <th className="w-[100px] text-center">ACTION</th>
                </tr>
              </thead>
              <tbody>
                {isLoading || data.items.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center">
                      {isLoading ? "Loading..." : "No data available"}
                    </td>
                  </tr>
                ) : (
                  data.items.map((item, index) => (
                    <tr key={item.location_warehouse_id}>
                      <td>{TableNumber({ pageCurrent: data.pagination_data.page, dataLength: data.pagination_data.size, index, limit: pagination.limit, totalData: data.pagination_data.total })}</td>
                      <td>{item.warehouse_code}</td>
                      <td>{item.location_name ?? "-"}</td>
                      <td>{item.warehouse_name}</td>
                      <td>{item.description ?? "-"}</td>
                      <td>
                        <div className="flex justify-center">
                          <ActionButton.Update onClick={() => setEditData(item)} />
                          <ActionButton.Delete onClick={() => setDeleteData(item)} />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </BaseTable>
          </div>

          <div className="mt-5">
            <PaginationData data={data} pagination={pagination} setPagination={setPagination} />
          </div>
        </Card.Body>
      </Card>

      <FooterMenu />

      {createModal.isActive && <CreateModal toggle={createModal.toggle} refetch={fetchData} locationOptions={locationOptions} locationLoading={locationLoading} />}
      {editData && <UpdateModal data={editData} toggle={() => setEditData(null)} refetch={fetchData} locationOptions={locationOptions} locationLoading={locationLoading} />}
      {deleteData && <DeleteModal data={deleteData} toggle={() => setDeleteData(null)} refetch={fetchData} />}
    </section>
  )
}
